import { useEffect, useMemo, useState } from 'react';
import { alpha, Box, ButtonBase, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllSendingDates } from 'src/shared/services/sendingService';

// Antal dagar framåt som är stängt för bokning (bokningsstopp).
const BOOKING_DEADLINE_DAYS = 7;
const WEEKDAYS = ['Sön', 'Mån', 'Tis', 'Ons', 'Tors', 'Fre', 'Lör'];
const MONTHS = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];

interface DateOption {
  id: number;
  // Riktigt DB-datum – används vid val (onChange) och alla beräkningar (medlemsår/friperiod).
  date: Dayjs;
  // Kundens visningsdatum = DB-datumet − 1 dag. Styr enbart etikett + månadsgruppering, inte val/beräkning.
  displayDate: Dayjs;
}

interface SendingDatePickerProps {
  value: number | string | undefined;
  onChange: (id: number, date: Dayjs) => void;
  // Valfritt intervall: datum utanför det visas inte alls. Används av påminnelserna, där bara datum efter
  // föregående utskick och inom rimlig framförhållning är meningsfulla.
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
  // Text som visas när inget datum finns i intervallet.
  emptyText?: string;
}

// Väljare av utskicksdatum bland förbundets fördefinierade utskicksdatum (grupperade per månad). Delas av
// skapa-flödet (steg 3) och redigeringsmodalen på beställningssidan.
export const SendingDatePicker = ({ value, onChange, minDate = null, maxDate = null, emptyText }: SendingDatePickerProps) => {
  const isMounted = useIsMounted();
  const [dates, setDates] = useState<DateOption[]>([]);

  useEffect(() => {
    const fetchSendingDates = async () => {
      try {
        const result = await getAllSendingDates(0, 100, 'sndDate', '', BOOKING_DEADLINE_DAYS);
        if (isMounted()) {
          const mapped = result.data.results
            .map((item) => ({ id: item.sndDate_id, date: dayjs(item.sndDate), displayDate: dayjs(item.sndDate).subtract(1, 'day') }))
            .sort((a, b) => a.date.valueOf() - b.date.valueOf());
          setDates(mapped);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    };
    fetchSendingDates();
  }, [isMounted]);

  // Begränsa till det meningsfulla intervallet innan gruppering (filtreras på DB-datumet, samma som valet gäller).
  const selectable = useMemo(
    () => dates.filter((d) => !(minDate && d.date.isBefore(minDate, 'day')) && !(maxDate && d.date.isAfter(maxDate, 'day'))),
    [dates, minDate, maxDate],
  );

  // Gruppera de valbara datumen per månad (kalenderkänsla).
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; items: DateOption[] }>();
    selectable.forEach((d) => {
      // Gruppera på kundens visningsdatum (−1 dag) så månadssektionen matchar den visade dagen.
      const key = `${d.displayDate.year()}-${d.displayDate.month()}`;
      const existing = map.get(key);
      if (existing) existing.items.push(d);
      else map.set(key, { label: `${MONTHS[d.displayDate.month()]} ${d.displayDate.year()}`, items: [d] });
    });
    return Array.from(map.values());
  }, [selectable]);

  if (selectable.length === 0) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        {emptyText ?? 'Inga valbara utskicksdatum tillgängliga just nu.'}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
      {groups.map((group) => (
        <Box key={group.label}>
          <Typography variant='subtitle2' sx={{ color: 'text.secondary', mb: 1 }}>
            {group.label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: 430 }}>
            {group.items.map((d) => {
              const selected = String(value) === String(d.id);
              return (
                <ButtonBase
                  key={d.id}
                  onClick={() => onChange(d.id, d.date)}
                  sx={{
                    position: 'relative',
                    width: 78,
                    py: 1,
                    flexDirection: 'column',
                    borderRadius: 1.5,
                    border: '2px solid',
                    borderColor: selected ? 'success.main' : 'divider',
                    bgcolor: (theme) => (selected ? alpha(theme.palette.success.light, 0.2) : '#fff'),
                    transition: 'border-color .15s, background-color .15s',
                    '&:hover': {
                      borderColor: 'success.main',
                      bgcolor: (theme) => alpha(theme.palette.success.light, 0.12),
                    },
                  }}
                >
                  <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1 }}>
                    {WEEKDAYS[d.displayDate.day()]}
                  </Typography>
                  <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {d.displayDate.date()}
                  </Typography>
                  {selected && <CheckCircle sx={{ position: 'absolute', top: 3, right: 3, fontSize: 16, color: 'success.main' }} />}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
